import * as Yup from 'yup'
import React from 'react'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-hot-toast'

import Drawer from '../Drawer'
import Spinner from '../Spinner'
import BuzzlUserEditForm from './BuzzlUserEditForm'
import BuzzlUserAddForm from './BuzzlUserAddForm'
import ErrorPage from '../ErrorPage'
import buzzlUserService from '../../services/buzzlUserService'
import { BuzzlUser, BuzzlUserDTO, BuzzlUsersError } from '../../types/buzzl-user'
import { buzzlRoles } from '../../utils/config'
import { queryClient } from '../../utils/react-query'
import DeleteButton from '../DeleteButton'
import customerService from '../../services/customerService'

type BuzzlUserDrawerProps = {
  onClose: () => void
  open: boolean
  selectedUser?: BuzzlUser
}

const schema = Yup.object().shape({
  role: Yup.string().oneOf(['OPERATOR', 'ADMIN']).required(),
  // displayName: Yup.string().required(),
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  email: Yup.string().email().required(),
  customers: Yup.array().of(Yup.string()),
})

const BuzzlUserDrawer: React.FC<BuzzlUserDrawerProps> = ({ onClose, open, selectedUser }) => {
  const {
    data: customers,
    isLoading,
    isError,
  } = useQuery(['customers'], () => customerService.getAll({ search: '' }), {
    select: (data) => {
      return data.map(({ name, id }) => ({ label: name, value: id }))
    },
  })

  const newBuzzlUser = useMutation(buzzlUserService.createBuzzlUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('buzzlUsers')
      toast.success('Account created.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const updateBuzzlUser = useMutation(buzzlUserService.updateBuzzlUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('buzzlUsers')
      toast.success('Account updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const deleteBuzzlUser = useMutation(buzzlUserService.deleteBuzzlUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('buzzlUsers')
      toast.success('Account deleted.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues: BuzzlUserDTO = {
    role: selectedUser?.role || buzzlRoles.admin,
    // displayName: selectedUser?.user?.displayName || '',
    lastName: selectedUser?.user?.lastName || '',
    firstName: selectedUser?.user?.firstName || '',
    email: selectedUser?.user?.email || '',
    customers:
      selectedUser?.assignedCustomers && selectedUser.assignedCustomers.length > 0
        ? selectedUser.assignedCustomers.map(({ id }) => id)
        : [],
  }

  const handleOnSubmit = async (values: BuzzlUserDTO, formikBag: FormikHelpers<BuzzlUserDTO>) => {
    try {
      if (selectedUser) {
        await updateBuzzlUser.mutateAsync({ id: selectedUser.id, ...values })
      } else {
        await newBuzzlUser.mutateAsync(values)
      }
      onClose()
    } catch (e) {
      const error = e as BuzzlUsersError

      error.response.errors.forEach((err) => {
        if (err.extensions.code === 'buzzl-user/email-already-exists') {
          formikBag.setFieldError('email', err.message)
        }
      })
    }
  }

  if (isLoading) return null

  return (
    <Drawer open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, dirty, values }: FormikProps<BuzzlUserDTO>) => (
          <Drawer.FormContainer
            open={open}
            onClose={onClose}
            title="Add Buzzjar User"
            Footer={
              !isError ? (
                <>
                  <button className="btn btn-white" onClick={onClose} disabled={isSubmitting} type="button">
                    Cancel
                  </button>

                  {selectedUser && (
                    <DeleteButton
                      type="button"
                      className="btn btn-delete ml-4"
                      modalTitle="Delete account"
                      modalText="Once you delete your account, you will lose all data associated with
                    it. This action is irreversible. Please continue
                    carefully."
                      disabled={deleteBuzzlUser.isLoading}
                      onClick={async () => {
                        await deleteBuzzlUser.mutateAsync(selectedUser.id)
                        onClose()
                      }}
                    >
                      {deleteBuzzlUser.isLoading ? (
                        <div className="mr-3">
                          <Spinner className="trailing-icon h-4 w-4" />
                        </div>
                      ) : null}
                      Delete account
                    </DeleteButton>
                  )}

                  <button className="btn btn-primary ml-4" type="submit" disabled={isSubmitting || !dirty}>
                    {isSubmitting ? (
                      <>
                        <Spinner className="h-5 w-5 text-gray-700" aria-hidden="true" />
                        Loading
                      </>
                    ) : selectedUser ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </button>
                </>
              ) : (
                <></>
              )
            }
          >
            {isError || !customers ? (
              <ErrorPage
                title="Something went wrong!"
                text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
              />
            ) : selectedUser ? (
              <BuzzlUserEditForm
                isSubmitting={isSubmitting}
                customers={customers}
                showCustomers={values.role === buzzlRoles.operator}
              />
            ) : (
              <BuzzlUserAddForm
                isSubmitting={isSubmitting}
                customers={customers}
                values={values}
                showCustomers={values.role === buzzlRoles.operator}
              />
            )}
          </Drawer.FormContainer>
        )}
      </Formik>
    </Drawer>
  )
}

export default BuzzlUserDrawer
